import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { Plus, Clock, ChevronRight, Image as ImageIcon, Pencil } from "lucide-react";
import SidePanel from "./SidePanel";
import PlanCardActions from "./PlanCardActions";

const today = () => new Date().toISOString().slice(0, 10);

function PreorderCard({ plan, onClick, onEdit, todayStats, onChanged }) {
  const archived = !!plan.archived;
  const inactive = plan.active === false;
  return (
    <div
      data-testid={`preorder-card-${plan.id}`}
      className={`text-left border rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer ${archived ? "bg-amber-50/40 border-amber-200 opacity-90" : "bg-amber-50 border-amber-200"}`}
      onClick={onClick}
    >
      <div className="h-32 bg-amber-100 flex items-center justify-center relative">
        {plan.image_url ? (
          <img src={plan.image_url} alt="" className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <ImageIcon size={36} className="text-amber-300" />
        )}
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white/90 text-amber-900">
          <Clock size={10} /> {plan.cutoff_time || "16:00"} cutoff
        </span>
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <button data-testid={`preorder-edit-${plan.id}`} onClick={(e) => { e.stopPropagation(); onEdit(plan); }}
            className="p-1.5 rounded-full bg-white/90 text-amber-900 hover:bg-white" title="Edit menu">
            <Pencil size={11} />
          </button>
          <div className="bg-white/90 rounded-full">
            <PlanCardActions plan={plan} resource="meal-plans" accentClass="bg-white text-amber-900"
              onChanged={onChanged} testid="pre" />
          </div>
        </div>
        <span className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-900 text-white capitalize">
          {plan.slots?.[0] || "—"}
        </span>
        {(archived || inactive) && (
          <span className={`absolute bottom-2 right-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded ${archived ? "bg-amber-200 text-amber-900" : "bg-slate-200 text-slate-700"}`}>
            {archived ? "Archived" : "Inactive"}
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-heading text-lg font-extrabold text-amber-900 tracking-tight">{plan.name}</h3>
            <div className="text-xl font-extrabold text-amber-950">{inr(plan.price || 0)}</div>
          </div>
        </div>
        {plan.menu_items?.length > 0 && (
          <div className="text-xs text-amber-900 mb-3 line-clamp-2">
            {plan.menu_items.join(" · ")}
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-amber-200">
          <div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-amber-800">Orders</div>
            <div className="font-heading text-lg font-extrabold text-amber-950">{todayStats?.upcoming_count ?? "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-amber-800">Collected</div>
            <div className="font-heading text-lg font-extrabold text-emerald-700">{todayStats?.collected ?? "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-amber-800">Uncollected</div>
            <div className="font-heading text-lg font-extrabold text-rose-700">{todayStats?.uncollected ?? "—"}</div>
          </div>
        </div>
        <div className="flex justify-end mt-2">
          <ChevronRight size={14} className="text-amber-600 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </div>
  );
}

function groupByDay(rows, dateKey) {
  const map = {};
  for (const r of rows || []) {
    const k = r[dateKey] || "(no date)";
    (map[k] = map[k] || []).push(r);
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

function PreorderDetail({ plan, onClose }) {
  const [tab, setTab] = useState("orders");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!plan?.id) return;
    setLoading(true);
    api.get(`/meal-plans/${plan.id}/orders-and-consumption`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [plan?.id]);

  const upcomingByDay = useMemo(() => groupByDay(data?.upcoming, "scheduled_date"), [data]);
  const pastByDay = useMemo(() => groupByDay(data?.past, "date"), [data]);

  return (
    <SidePanel open={!!plan} onClose={onClose} accent="amber" title={plan?.name}>
      {/* Menu items + image */}
      {plan.image_url && (
        <img src={plan.image_url} alt="" className="w-full h-32 object-cover rounded-xl mb-4"
          onError={(e) => { e.target.style.display = "none"; }} />
      )}
      {plan.menu_items?.length > 0 && (
        <div className="mb-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-2">Menu items</h3>
          <div className="flex flex-wrap gap-1.5">
            {plan.menu_items.map((m, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-amber-100 text-amber-900 rounded-lg">{m}</span>
            ))}
          </div>
        </div>
      )}

      <div className="inline-flex bg-amber-100 rounded-full p-1 mb-5">
        {[
          { key: "orders", label: "Upcoming orders" },
          { key: "past", label: "Past consumption" },
          { key: "setup", label: "Setup" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`preorder-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${tab === t.key ? "bg-amber-900 text-white" : "text-amber-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-amber-800">Loading…</div>}

      {!loading && tab === "orders" && (
        <div className="space-y-4">
          {upcomingByDay.length === 0 && (
            <div className="p-6 border border-dashed border-amber-200 rounded-xl text-center text-sm text-amber-700">No upcoming orders.</div>
          )}
          {upcomingByDay.map(([day, rows]) => (
            <div key={day} className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="bg-amber-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center justify-between">
                <span>{day}</span>
                <span>{rows.length} order{rows.length > 1 ? "s" : ""}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.student_plan_id || i} className="border-t border-amber-100 first:border-t-0" data-testid={`upcoming-order-${day}-${i}`}>
                      <td className="p-2 font-semibold">{r.student_name}</td>
                      <td className="p-2 text-xs text-amber-700">{r.student_id_display}</td>
                      <td className="p-2 text-xs text-amber-800">{r.grade || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "past" && (
        <div className="space-y-4">
          {pastByDay.length === 0 && (
            <div className="p-6 border border-dashed border-amber-200 rounded-xl text-center text-sm text-amber-700">No past consumption.</div>
          )}
          {pastByDay.map(([day, rows]) => (
            <div key={day} className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="bg-amber-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center justify-between">
                <span>{day}</span>
                <span>{rows.length} taps</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-amber-100 first:border-t-0" data-testid={`past-consumption-${day}-${i}`}>
                      <td className="p-2 font-semibold">{r.student_name}</td>
                      <td className="p-2 text-xs text-amber-700 capitalize">{r.slot}</td>
                      <td className="p-2 text-xs text-amber-800">{r.grade || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "setup" && (
        <div className="space-y-3 text-sm">
          <div><strong>Slot:</strong> <span className="capitalize">{plan.slots?.[0] || "—"}</span></div>
          <div><strong>Price:</strong> {inr(plan.price || 0)}</div>
          <div><strong>Cutoff:</strong> {plan.cutoff_time || "16:00"} on previous working day</div>
          <div><strong>Available days:</strong> {(plan.available_days || ["mon","tue","wed","thu","fri"]).join(", ")}</div>
          <div className="text-xs text-amber-700 pt-2 border-t border-amber-200">
            Re-create via the top-right "Create Plan ▾" button to edit price, cutoff or days.
          </div>
        </div>
      )}
    </SidePanel>
  );
}

export default function PreorderTab({ plans, onCreate, onEdit, onPlanUpdated, showArchived }) {
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    plans.forEach(p => {
      api.get(`/meal-plans/${p.id}/orders-and-consumption?startDate=${today()}&endDate=${today()}`)
        .then(r => {
          const collected = (r.data?.past || []).length;
          const upcoming = (r.data?.upcoming || []).length;
          setStats(prev => ({ ...prev, [p.id]: { upcoming_count: upcoming, collected, uncollected: Math.max(0, upcoming - collected) } }));
        })
        .catch(() => {});
    });
  }, [plans]);

  if (plans.length === 0) {
    return (
      <div className="border-2 border-dashed border-amber-200 rounded-2xl p-12 text-center bg-amber-50/50">
        <h3 className="font-heading text-xl font-bold text-amber-900 mb-2">{showArchived ? "No archived pre-order menus" : "No pre-order menus yet"}</h3>
        <p className="text-sm text-amber-800 mb-4">{showArchived ? "Archived pre-order menus will appear here." : "Pre-order menus let parents book a daily meal (breakfast / lunch / snacks) up to 4 PM the previous working day."}</p>
        {!showArchived && (
          <button onClick={() => onCreate("preorder")} data-testid="empty-create-preorder"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-700 text-white font-semibold hover:bg-amber-800">
            <Plus size={14} /> Create Pre-order Menu
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {plans.map(p => (
          <PreorderCard key={p.id} plan={p} onClick={() => setSelected(p)} onEdit={onEdit}
            todayStats={stats[p.id]} onChanged={onPlanUpdated} />
        ))}
      </div>
      {selected && <PreorderDetail plan={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
